/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [leave-space-dialog.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 05.04.20, 18:41
 */

import React, {useEffect, useState} from 'react';
import {useAuthorization} from "../../selectors";
import {useLanguage} from "../configuration/configuration";
import {Button} from "../navigation/buttons/nav-buttons";
import {NavigationGroup, NavigationRow} from "../navigation/layout/nav-layout";
import {FormRow} from "../forms/manage-spaces/manage-space-form";
import FormMarkdownTextarea from "../toggler/form-markdown-textarea";
import useUpdateMedia from "../hooks/use-update-media";
import {PENDING_STATUS} from "../../actions";
import {Spinner} from "../util/spinner";
import {CloseIcon} from "../navigation/icons/nav-icons";

/**
 * a simple form to update media title and description in a popover dialog
 * popover is kind of a psychotic ground for displaying dialogs
 * this component collects only data and passes it to the parent on submitting
 */
const EditGalleryMediaPopover = ({spaceId, mediaId, title='', description ='',
                                     onUpdate,
                                     onClose}) => {
    const [formData, setFormData] = useState({
        spaceId: spaceId,
        mediaId: mediaId,
        title: title, // imageTitle
        description: description, // imageCaption
        });
    const [isOpen, setIsOpen] = useState(true);
    const [invalid, setInvalid] = useState(false);
    const {isAuthorized} = useAuthorization();
    const {updateSpaceMedia, updateMedia, media, status, error} = useUpdateMedia()
    const Lang = useLanguage();

    // prevent lightbox from scrolling with keys while editor open
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event) => {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === '') {
                event.stopPropagation();
            }
        }
        window.addEventListener('keydown', handleKeyDown, true);
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        }
    }, [spaceId, mediaId])

    const updateGalleryMedia = () => {
        updateSpaceMedia(spaceId, formData, (updatedSpace) => {
            onUpdate && onUpdate(formData);
        });
    }

    const handleOnChange = (event) => {
        setFormData({...formData, [event.target.name]: event.target.value});
    }

    const handleSubmit = (event) => {
        event.target.classList.add('was-validated');
        event.preventDefault();
        event.stopPropagation();

        if (!event.target.checkValidity()) {
            setInvalid(true);
            return;
        }
        setInvalid(false);
        updateGalleryMedia();
    }

    const handleOnClose = () => {
        setIsOpen(false);
        onClose && onClose();
    }

    if (status === PENDING_STATUS) return <Spinner/>
    if (!isAuthorized) return null;

    return <div className='px-2 py-1'>
        <NavigationRow>
            <NavigationGroup>
                <h4 className='clr-popover-search'>{Lang.gallery.popover.updateGalleryMedia}</h4>
            </NavigationGroup>
            <NavigationGroup>
                <button className='btn btn-outline-primary m-0 p-1' onClick={handleOnClose}>
                    <CloseIcon big className='clr-popover-search'/>
                </button>
            </NavigationGroup>
        </NavigationRow>

        <form noValidate className={`needs-validation ${invalid ? 'form-invalid' : ''} text-start`} onSubmit={handleSubmit}>
            <FormRow className='mb-2'>
                <label className="form-check-label float-left"
                       htmlFor="titleId">{Lang.updateSpace.mediaTitle.text}</label>
                <input className='form-control' type="text" name="title"
                       id='titleId'
                       placeholder={Lang.updateSpace.mediaTitle.placeholder}
                       value={formData.title}
                       onChange={handleOnChange}/>
            </FormRow>

            <FormMarkdownTextarea className='form-control' name="description"
                                  label={Lang.updateSpace.description.text}
                                  placeholder={Lang.updateSpace.description.placeholder}
                                  id='descriptionId'
                                  value={formData.description}
                                  maxLength='512'
                                  onChange={handleOnChange}/>

            <FormRow className='mt-2 mb-2'>
                <Button block
                        type="submit">{Lang.gallery.popover.updateGalleryMedia}</Button>
            </FormRow>
        </form>
    </div>
};

export default EditGalleryMediaPopover;